/* eslint-disable react/no-string-refs */
import React from 'react';
import { FormattedMessage } from 'react-intl';
import { PropTypes as PT } from 'prop-types';
import { media } from '@lifesize/nucleus';
import { media as sdkMedia } from '@lifesize/clients.sdk';
import SVG from 'react-inlinesvg';
import cancelIcon from '@lifesize/ux-assets/vectors/svgIcons/cancel.svg';
import peopleIcon from '@lifesize/ux-assets/vectors/svgIcons/people.svg';
import personIcon from '@lifesize/ux-assets/vectors/svgIcons/person.svg';
import incomingRing from 'assets/audio/incoming-ring.mp3';
import Button from 'components/Button/Button';
import LockedMeetingTooltip from 'components/Tooltip/LockedMeetingTooltip';
import PulsingAvatar from 'components/PulsingAvatar/PulsingAvatar';
import CallPreview from 'components/CallPreview/CallPreview';
import AudioElement from 'containers/AudioElement/AudioElementContainer';
import classes from './IncomingCallModal.module.scss';

/**
 * This class is used as the holding component for all full screen modals
 * in the app. We just perform a simple switch on the subcomponent type to be
 * rendered in the full screen modal.
 */

export class IncomingCallModal extends React.Component {
  renderAudioTag = () => (
    this.props.isCallConnected ? null : <AudioElement
      src={incomingRing}
      autoPlay
      loop
    />
  );

  handleIgnore = () => {
    const { call, ignore, isCallConnected } = this.props;
    if (!isCallConnected) {
      sdkMedia.stopWebcam();
    }
    ignore(call);
  }

  renderButtons() {
    const buttons = [];
    const { call, isLockedByModerator } = this.props;
    if (call && call.canAdd) {
      buttons.push(
        <div key="add">
          <Button
            disabled={isLockedByModerator}
            onClick={() => {
              this.props.add(call);
            }}
            dataAttrs={{ 'data-testid': 'add-call-btn' }}
          >
            {isLockedByModerator && <LockedMeetingTooltip />}
            <FormattedMessage
              id="components.call.add"
              defaultMessage="Add"
            />
          </Button>
        </div>
      );
    }

    if (this.props.isCallConnected) {
      buttons.push(
        <div key="endAndAnswer">
          <Button
            kind="secondary"
            onClick={() => {
              this.props.endAndAnswer(call);
            }}
            dataAttrs={{ 'data-testid': 'end-and-answer-call-btn' }}
          >
            <FormattedMessage
              id="components.call.endAndAnswer"
              defaultMessage="Leave + Answer"
            />
          </Button>
        </div>
      );
    }

    buttons.push(
      <div key="ignore">
        <Button
          kind="secondary"
          onClick={this.handleIgnore}
          dataAttrs={{ 'data-testid': 'ignore-call-btn' }}
        >
          <FormattedMessage
            id="components.call.ignore"
            defaultMessage="Ignore"
          />
        </Button>
      </div>
    );

    // Its the first call show answer button
    if (!this.props.isCallConnected) {

      buttons.push(
        <div key="answer">
          <Button
            disabled={!this.props.localPrimaryStream}
            onClick={() => {
              this.props.answer(call);
            }}
            dataAttrs={{ 'data-testid': 'answer-call-btn' }}
          >
            <FormattedMessage
              id="components.call.answer"
              defaultMessage="Answer"
            />
          </Button>
        </div>
      );
    }

    return (
      <div className={classes.buttonBlock}>
        {buttons}
      </div>
    );
  }

  render() {
    const {
      isVisible
    } = this.props;

    if (!isVisible) return null;

    if (this.props.isCallConnected) {
      return (
        <div
          className={classes.shortContainer}
        >
          {this.renderAudioTag()}
          <div className={classes.modalContent}>
            <button
              ref="closeModalButton"
              id="closeModalButton"
              className={classes.closeButton}
              aria-label="Close Full Screen Modal"
              onClick={this.handleIgnore}
            >
              <SVG scr={cancelIcon} />
            </button>
            <article className={classes.callContainer}>
              <header>
                <h2
                  className={classes.modalHeader}
                >
                  <FormattedMessage
                    id="components.call.title"
                    defaultMessage="Incoming Call"
                  />
                </h2>
                <div
                  className={classes.callName}
                >
                  {this.props.displayString}
                </div>
              </header>
              <div className={classes.verticalButtons}>
                {this.renderButtons()}
              </div>
            </article>
          </div>
        </div>);
    }

    return (
      <div
        className={classes.container}
      >
        {this.renderAudioTag()}
        <div className={classes.modalContent}>
          <button
            ref="closeModalButton"
            id="closeModalButton"
            className={classes.closeButton}
            aria-label="Close Full Screen Modal"
            onClick={this.handleIgnore}
          >
            <SVG src={cancelIcon} data-testid="cancelIcon" />
          </button>
          <article className={classes.callContainer}>
            <header>
              <h2
                className={classes.modalHeader}
              >
                <FormattedMessage
                  id="components.call.title"
                  defaultMessage="Incoming Call"
                />
              </h2>
              <div
                className={classes.callName}
              >
                {this.props.displayString}
              </div>
            </header>
            <PulsingAvatar
              icon={this.props.isIncomingCallMeeting ? peopleIcon : personIcon}
            />
            {<CallPreview
              isVideoMuted={this.props.isVideoMuted}
              isAudioMuted={this.props.isAudioMuted}
              defaultVideoSetting={!this.props.defaultVideoMute}
              defaultAudioSetting={!this.props.defaultAudioMute}
              onVideoMuteClick={() => media.setVideoMute(!this.props.isVideoMuted)}
              onAudioMuteClick={() => media.setAudioMute(!this.props.isAudioMuted)}
              inActiveCall={this.props.isCallConnected}
            />}
            {this.renderButtons()}
          </article>
        </div>
      </div>
    );
  }
}

IncomingCallModal.propTypes = {
  isIncomingCallMeeting: PT.bool,
  add: PT.func.isRequired,
  answer: PT.func.isRequired,
  call: PT.object,
  defaultAudioSetting: PT.bool,
  endAndAnswer: PT.func.isRequired,
  ignore: PT.func.isRequired,
  intl: PT.object,
  isLockedByModerator: PT.bool,
  isVisible: PT.bool.isRequired,
  isAudioMuted: PT.bool,
  isVideoMuted: PT.bool,
  defaultVideoMute: PT.bool,
  defaultAudioMute: PT.bool,
  localPrimaryStream: PT.object,
};

export default IncomingCallModal;
